import { getAnalytics, isSupported as isAnalyticsSupported } from "firebase/analytics";
import { initializeApp } from "firebase/app";
import { getFirestore } from "firebase/firestore";
import { getMessaging, isSupported as isMessagingSupported, onMessage } from "firebase/messaging";
import defaultImage from "../src/assets/icons/ic_user_default.svg";
import { NOTIFICATION_OBJECT_TYPE } from "./constants/status";
import { PayloadFirebaseNotify } from "./models/response/notification/notification";

type ToastClickHandler = (payload: PayloadFirebaseNotify) => void;

class NotificationManager {
  private queue: PayloadFirebaseNotify[] = [];
  private activeToasts: HTMLElement[] = [];
  private readonly MAX_VISIBLE_TOASTS = 3;
  private readonly TOAST_DURATION = 5000;
  private readonly INITIAL_TOP_OFFSET = 58; // Khoảng cách từ top của màn hình
  private readonly TOAST_SPACING = 10; // Khoảng cách tối thiểu giữa các toast
  private isProcessing = false;
  private onClickHandler?: ToastClickHandler;
  private notificationSound: HTMLAudioElement;

  constructor() {
    this.notificationSound = new Audio("/notification.mp3");
  }

  private playSound() {
    // Tạo instance mới để cho phép overlap âm thanh nếu nhiều toast cùng lúc
    const sound = this.notificationSound.cloneNode() as HTMLAudioElement;
    sound.play().catch((err) => {
      console.warn("Unable to play sound:", err);
    });
  }

  setOnClickHandler(handler: ToastClickHandler) {
    this.onClickHandler = handler;
  }

  addNotification(payload: PayloadFirebaseNotify) {
    this.queue.push(payload);
    this.processQueue();
  }

  private async processQueue() {
    if (this.isProcessing || this.queue.length === 0) {
      return;
    }

    if (this.activeToasts.length >= this.MAX_VISIBLE_TOASTS) {
      return;
    }

    this.isProcessing = true;
    const payload = this.queue.shift();

    if (payload) {
      const toast = this.createToast(payload);
      this.activeToasts.push(toast);

      // Auto remove after duration
      setTimeout(() => {
        this.removeToast(toast);
      }, this.TOAST_DURATION);
    }

    this.isProcessing = false;

    // Continue processing queue after a short delay
    setTimeout(() => {
      this.processQueue();
    }, 100);
  }

  private calculateToastPosition(): number {
    // Toast đầu tiên luôn ở vị trí INITIAL_TOP_OFFSET
    if (this.activeToasts.length === 0) {
      return this.INITIAL_TOP_OFFSET;
    }

    // Với các toast tiếp theo, tính từ vị trí đầu tiên cộng với chiều cao của các toast trước đó
    let totalHeight = this.INITIAL_TOP_OFFSET;

    this.activeToasts.forEach((toast, index) => {
      if (index !== 0) {
        const toastHeight = toast.offsetHeight || 0;
        totalHeight += toastHeight + this.TOAST_SPACING;
      }
    });

    return totalHeight;
  }

  private createToast(payload: PayloadFirebaseNotify): HTMLElement {
    const title = payload?.title ?? "Thông báo mới";
    const message = payload?.message ?? "Bạn có một thông báo mới";
    const avatar = payload?.object_type === NOTIFICATION_OBJECT_TYPE.USER ? payload?.user_notification?.avatar ?? "" : payload?.image_url ?? "";

    const toast = document.createElement("div");

    toast.className =
      "fixed right-3 border bg-white dark:bg-[#0e1627] p-3 pl-4 rounded-lg shadow-lg min-w-64 max-w-[25rem] z-[9999] cursor-pointer transition-all duration-500 ease-in-out";
    toast.style.top = `-200px`; // Start from above screen
    toast.style.opacity = "0";

    toast.innerHTML = `
      <span class="absolute top-1/2 left-1 w-1.5 h-[90%] rounded-full bg-[#1462B0] -translate-y-1/2"></span>
      <div class="flex justify-between items-center cursor-pointer">
        <div class="flex justify-center items-center">
          <div class="flex justify-center items-center mr-3 text-blue-500">
           <img src="${avatar}" onerror="this.onerror=null; this.src='${defaultImage}';" alt="Avatar" class="w-10 h-10 rounded-full" />
          </div>
          <div class="flex-1 mr-3">
            <div class="mb-1 text-base font-bold text-gray-600 font-nunito dark:text-white">${title}</div>
            <div class="text-medium font-nunito text-[#868e96] dark:text-[#828282]">${message}</div>
          </div>
        </div>
        <button type="button" class="toast-close bg-transparent border-none text-xl cursor-pointer w-7 h-7 rounded-md hover:bg-[#f1f3f5] ml-auto leading-none hover:text-gray-700 transition-colors">×</button>
      </div>
    `;

    // Add toast to body first (invisible)
    document.body.appendChild(toast);

    // Wait for DOM to render and calculate position
    setTimeout(() => {
      const topPosition = this.calculateToastPosition();
      toast.style.top = `${topPosition}px`;
      toast.style.opacity = "1";
      this.playSound();
    }, 10);

    // Handle click events
    toast.addEventListener("click", (event) => {
      if (!(event.target as HTMLElement).closest(".toast-close")) {
        this.onClickHandler?.(payload);
        this.removeToast(toast);
      }
    });

    // Handle close button
    const closeButton = toast.querySelector(".toast-close");
    if (closeButton) {
      closeButton.addEventListener("click", (e) => {
        e.stopPropagation();
        this.removeToast(toast);
      });
    }

    return toast;
  }

  private removeToast(toast: HTMLElement) {
    const index = this.activeToasts.indexOf(toast);
    if (index === -1) return;

    // Remove from active toasts array
    this.activeToasts.splice(index, 1);

    // Animate slide up and fade out
    toast.style.right = "-500px";
    toast.style.opacity = "0";

    // Remove from DOM after animation
    setTimeout(() => {
      if (document.body.contains(toast)) {
        document.body.removeChild(toast);
      }
    }, 500);

    // Reposition remaining toasts
    this.repositionToasts();

    // Process queue after removal
    setTimeout(() => {
      this.processQueue();
    }, 100);
  }

  private repositionToasts() {
    let currentTop = this.INITIAL_TOP_OFFSET;

    this.activeToasts.forEach((toast) => {
      toast.style.top = `${currentTop}px`;
      // Cộng thêm chiều cao thực tế của toast hiện tại + spacing
      currentTop += toast.offsetHeight + this.TOAST_SPACING;
    });
  }

  // Method để tính toán lại vị trí khi có thay đổi kích thước
  public recalculatePositions() {
    this.repositionToasts();
  }
}

const notificationManager = new NotificationManager();

export const firebaseConfig ={
  apiKey: "AIzaSyDhlWJTF8uObfyNdntvBUd64d6wqnN72Uc",
  authDomain: "gas-mobile-firebase.firebaseapp.com",
  projectId: "gas-mobile-firebase",
  storageBucket: "gas-mobile-firebase.firebasestorage.app",
  messagingSenderId: "498913008388",
  appId: "1:498913008388:web:726b65438c4ae9a3548e51",
  measurementId: "G-RZ0R2T8RLE"
};

/**
 * Môi trường TẮT Firebase (đọc `CONFIG_ENVIRONMENT` trong public/env.json — file này được nạp ĐỒNG BỘ bằng script
 * trong <head> của public/index.html, nên `window.env` luôn có trước khi bundle chạy tới đây).
 */
const FIREBASE_DISABLED_ENVS = ["dev"];

// Kiểm tra router hiện tại + môi trường trước khi khởi tạo Firebase
const shouldInitializeFirebase = () => {
  const currentPath = window.location.pathname;
  if (currentPath.includes("/join-class/")) return false;
  const env = String(window.env?.CONFIG_ENVIRONMENT || "").trim().toLowerCase();
  return !FIREBASE_DISABLED_ENVS.includes(env);
};

// Khởi tạo Firebase có điều kiện
let app: any = null;
let analytics: any = null;
let db: any = null;
let messaging: any = null;

/**
 * Khởi tạo Firebase AN TOÀN — KHÔNG được ném lỗi lúc nạp module (trước đây `getMessaging` chạy thẳng ở cấp module:
 * trang chạy HTTP (không phải HTTPS / localhost) không có `navigator.serviceWorker` → Firebase ném TypeError ngay lúc
 * import → cả bundle dừng, trắng trang). Messaging / Analytics chỉ bật khi trình duyệt hỗ trợ (`isSupported`);
 * không hỗ trợ → để null, web chạy bình thường, chỉ không có thông báo đẩy.
 * Nơi dùng `messaging` phải `await firebaseReady` trước (khởi tạo là bất đồng bộ).
 */
export const firebaseReady: Promise<void> = (async () => {
  if (!shouldInitializeFirebase()) return;
  try {
    app = initializeApp(firebaseConfig);
    db = getFirestore(app);
  } catch (err) {
    console.warn("[firebase] Khởi tạo Firebase lỗi — bỏ qua:", err);
    app = null;
    return;
  }

  try {
    if (await isAnalyticsSupported()) analytics = getAnalytics(app);
  } catch (err) {
    console.warn("[firebase] Analytics không khả dụng:", err);
  }

  try {
    if (!(await isMessagingSupported())) {
      console.warn("[firebase] Trình duyệt không hỗ trợ Firebase Messaging (cần HTTPS / localhost) — tắt thông báo đẩy.");
      return;
    }
    messaging = getMessaging(app);
    // Đăng ký Service Worker chỉ khi Messaging được khởi tạo
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/firebase-messaging-sw.js").catch((err) => {
        console.error("Service Worker registration failed:", err);
      });
    }
  } catch (err) {
    console.warn("[firebase] Khởi tạo Messaging lỗi — tắt thông báo đẩy:", err);
    messaging = null;
  }
})();

/** Messaging sau khi khởi tạo xong (null = không hỗ trợ / đã tắt). */
export const getMessagingInstance = async () => {
  await firebaseReady;
  return messaging;
};

export const onMessageListener = () =>
  new Promise(async (resolve) => {
    try {
      await firebaseReady;
      if (!messaging) {
        resolve(null);
        return;
      }
      return onMessage(messaging, (payload) => {
        // Backend gửi DATA-ONLY cho web: đọc tiêu đề/nội dung từ payload.data
        // (mappingPayloadNotify). Fallback parse notification.body cho tương thích luồng cũ.
        let notify: PayloadFirebaseNotify;
        const d: any = payload?.data;
        if (d && Object.keys(d).length > 0) {
          notify = new PayloadFirebaseNotify({
            ...d,
            object_type: d.object_type != null ? Number(d.object_type) : 0,
            action_type: d.action_type != null ? Number(d.action_type) : 0,
            sender_id: d.sender_id != null ? Number(d.sender_id) : 0,
            type: d.type != null ? Number(d.type) : 0,
          });
        } else {
          notify = JSON.parse(payload?.notification?.body ?? "{}");
        }
        notificationManager.addNotification(notify);
        resolve(payload);
      });
    } catch (error) {
      console.error("Error in message listener:", error);
      resolve(null);
    }
  });

// Hàm hiển thị toast notification từ trên chạy xuống - Updated to use NotificationManager
export const showToastNotification = (payload: PayloadFirebaseNotify) => {
  notificationManager.addNotification(payload);
};

export { analytics, db, messaging };

export { notificationManager };
